import { Box, Icon, Stack, Theme } from '@mui/material';
import { Component } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Localized } from '@synergy/components';
import { PoolLineClampTypography } from '@synergy/components-next';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { PaymentTerm } from '../../../../../common/models/payment-term';
import { PriceMatrixFooterTerm } from './price-matrix-footer-term';

const styles = (theme: Theme) =>
    createStyles({
        section: {
            padding: theme.spacing(3),
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            lineHeight: '15px',
            '& span': {
                display: 'block',
            },
        },
        sectionIcons: {
            justifyContent: 'space-around',
        },
        total: {
            fontSize: '13px',
            lineHeight: '16px',
            color: '#21272E',
            fontWeight: 600,
        },
        left: {
            textAlign: 'left',
        },
        right: {
            textAlign: 'right',
        },
        summary: {
            backgroundColor: '#F6F6F6',
        },
        recommendationSection: {
            minHeight: 30,
        },
        recommendation: {
            minHeight: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            lineHeight: '14px',
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            '& span:first-child': {
                marginRight: 10,
                fontSize: '16px',
            },
        },
    });

interface PriceMatrixFooterProps extends WithStyles<typeof styles> {
    deliveryCost: number;
    surcharge: number;
    recommendation: boolean;
    total: number;
    currency: string;
    paymentTerm?: PaymentTerm;
    termsOfDeliveryContent?: string;
    supplierBonus?: string;
}

export const PriceMatrixFooter = withStyles(styles)(
    class PriceMatrixFooterBase extends Component<PriceMatrixFooterProps> {
        public render() {
            const {
                classes,
                deliveryCost,
                recommendation,
                total,
                surcharge,
                termsOfDeliveryContent,
                paymentTerm,
                supplierBonus,
            } = this.props;

            return (
                <div>
                    <Box sx={{ fontSize: 12, p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                            <PoolLineClampTypography sx={{ fontSize: 12 }}>
                                <FormattedMessage id="app.priceMatrix.surcharges" />
                            </PoolLineClampTypography>
                            <FormattedNumber style="currency" currency={this.props.currency} value={surcharge || 0} />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                            <FormattedMessage id="app.priceMatrix.deliveryCost" />
                            <Box>
                                <FormattedNumber style="currency" currency={this.props.currency} value={deliveryCost} />
                            </Box>
                        </Stack>
                    </Box>

                    <div className={`${classes.section} ${classes.sectionIcons}`}>
                        <PriceMatrixFooterTerm icon="icon-payment" tooltip={<Localized value={paymentTerm?.text} />} />
                        <PriceMatrixFooterTerm icon="icon-local_shipping" tooltip={termsOfDeliveryContent} />
                        <PriceMatrixFooterTerm icon="icon-loyalty" tooltip={supplierBonus} />
                    </div>

                    <div className={`${classes.section} ${classes.summary}`}>
                        <div className={classes.left}>
                            <span className={classes.total}>
                                <FormattedMessage id="app.priceMatrix.total" />
                            </span>
                        </div>
                        <div className={classes.right}>
                            <span className={classes.total}>
                                <FormattedNumber style="currency" currency={this.props.currency} value={total} />
                            </span>
                        </div>
                    </div>

                    <div className={classes.recommendationSection}>
                        {recommendation && (
                            <div className={classes.recommendation}>
                                <Icon className="icon-star" />
                                <FormattedMessage id="app.priceMatrix.recommendation" />
                            </div>
                        )}
                    </div>
                </div>
            );
        }
    }
);
